import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import {
  X, Save, Loader2, ChevronDown, ChevronRight, Sparkles,
} from 'lucide-react';

// ==================== CONSTANTS ====================
const AGE_RANGES = [
  '18-22', '23-27', '28-32', '33-37', '38-42', '43-47',
  '48-52', '53-57', '58-62', '63-67', '68-72', '70+',
];

const GENDER_OPTIONS = ['Female', 'Trans', 'Prefer not to say'];
const SESSION_LOCATIONS = ['In-person', 'Online'];
const SESSION_TYPES = ['Chat', 'Voice', 'Hybrid', 'Therapy session'];

const HORMONAL_STAGES = [
  'Menstruation (Aadhira)',
  'Pre/Post Natal (Aaraike)',
  'Perimenopause (Arivu)',
  'Menopause (Menomorphosis)',
];

const CONTEXTUAL_FACTORS = [
  'Work / Career',
  'Relationships / Family',
  'Health (diagnosed / suspected)',
  'Life transitions (e.g., new mother, caregiving, menopause onset)',
];

const INTENSITY_LEVELS = ['Low', 'Moderate', 'High'];
const AFFECT_OPTIONS = ['Congruent', 'Incongruent', 'Fluctuating'];
const HORMONAL_AWARENESS = ['Aware', 'Partially aware', 'Unaware'];

const KEY_THEMES = [
  'Body awareness / disconnect',
  'Identity shifts',
  'Burnout / exhaustion',
  'Relationship strain',
  'Guilt / shame',
  'Loss of control',
  'Transition anxiety',
];

const IDEATION_LEVELS = ['None', 'Passive', 'Active'];
const ABUSE_OPTIONS = ['None', 'Suspected', 'Confirmed'];
const YES_NO = ['Yes', 'No'];
const RISK_LEVELS = ['Low', 'Medium', 'High', 'Critical'];
const ACTIONS_TAKEN = ['Escalated', 'Monitored', 'Safety plan initiated'];

const PSYCHOEDUCATION = [
  'Hormonal stage explanation',
  'Symptom normalization',
  'Cycle awareness guidance',
  'Lifestyle insights (sleep, nutrition, stress)',
  'When to seek medical support',
];

const RECEPTIVITY = ['Low', 'Moderate', 'High'];
const FOLLOWUP_MODES = ['Call', 'Chat', 'Other'];
const STAGE_TAGS = ['Aadhira', 'Aaraike', 'Arivu', 'Menomorphosis'];
const SEVERITY_TAGS = ['Mild', 'Moderate', 'Severe'];
const USER_TYPES = ['First-time', 'Repeat'];

// Context-aware auto-suggestion word packs
const SUGGESTIONS = {
  trigger_onset_reasons: ['Workplace stress', 'Family conflict', 'Health diagnosis', 'Pregnancy', 'Postpartum', 'Relationship change', 'Menstrual cycle changes', 'Perimenopause onset', 'Sleep disruption', 'Grief / loss'],
  observed_emotional_state: ['Anxious', 'Low mood', 'Overwhelmed', 'Irritable', 'Tearful', 'Numb', 'Restless', 'Hopeful', 'Resigned', 'Hyper-alert'],
  cognitive_patterns: ['Rumination', 'Self-doubt', 'Catastrophizing', 'Perfectionism', 'Black-and-white thinking', 'Guilt spiral', 'Mind-reading', 'Hopeless thinking'],
  physical_symptoms: ['Fatigue', 'Sleep issues', 'Hot flashes', 'Cramps', 'Headaches', 'Brain fog', 'Weight changes', 'Palpitations', 'Joint pain', 'Heavy bleeding'],
  emotional_behavioural_patterns: ['PMS mood shifts', 'Perimenopausal irritability', 'Postpartum low', 'Cycle-linked anxiety', 'Libido changes', 'Social withdrawal', 'Cyclical overwhelm'],
  hormonal_awareness_notes: ['Unaware of cycle phases', 'Tracks cycle regularly', 'Confused about perimenopause', 'Blames self for symptoms', 'Aware but dismisses impact'],
  abuse_violence_notes: ['Controlling partner', 'Financial control', 'Verbal abuse', 'Past trauma disclosed', 'Unsafe home environment', 'Seeking support discreetly'],
  severe_distress_notes: ['Panic attacks', 'Dissociation', 'Severe insomnia', 'Intrusive thoughts', 'Functional decline', 'Unable to work'],
  medical_red_flags_notes: ['Heavy abnormal bleeding', 'Severe pelvic pain', 'Suspected pregnancy risk', 'Thyroid symptoms', 'Suspected PCOS', 'Suspected endometriosis', 'Cardiac symptoms'],
  interventions_used: ['Active listening', 'Emotional validation', 'Psychoeducation', 'Grounding', 'Reframing', 'Normalization', 'Resource sharing', 'Breathing / somatic practices'],
  insight_gained: ['Recognized cycle-mood link', 'Accepted life stage', 'Identified trigger', 'Saw pattern clearly', 'Felt validated', 'Willing to seek help'],
  emotional_shift: ['Calmer by end', 'Tearful release', 'Relieved', 'More grounded', 'Less self-blame', 'Opened up gradually'],
  resistance_barriers: ['Cultural stigma', 'Partner disapproval', 'Time / finance', 'Denial of symptoms', 'Mistrust', 'Fear of labeling'],
  session_quality_notes: ['Strong rapport', 'Slow start, deepened later', 'Held space well', 'Client led the session', 'Needed more containment'],
  what_worked_well: ['Psychoeducation landed', 'Grounding helped', 'Validation built trust', 'Normalization reduced shame', 'Clear next step defined'],
  areas_to_explore_next: ['Family of origin', 'Past medical history', 'Partner dynamics', 'Sleep hygiene', 'Cycle tracking', 'Referral need'],
};

// ==================== FIELD COMPONENTS ====================
const wordCount = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);

const Collapsible = ({ title, subtitle, open, onToggle, children, testId }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={testId}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 transition-colors"
      data-testid={`${testId}-toggle`}
    >
      <div className="flex items-center gap-2 text-left">
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </button>
    {open && <div className="p-4 space-y-4 bg-white">{children}</div>}
  </div>
);

const Label = ({ children, required }) => (
  <label className="block text-xs font-medium text-gray-700 mb-1">
    {children} {required && <span className="text-red-500">*</span>}
  </label>
);

const TextInput = ({ value, onChange, placeholder, readOnly, testId }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    readOnly={readOnly}
    placeholder={placeholder}
    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 ${readOnly ? 'bg-gray-50 text-gray-500' : ''}`}
    data-testid={testId}
  />
);

const SelectField = ({ value, onChange, options, placeholder = '— Select —', testId }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
    data-testid={testId}
  >
    <option value="">{placeholder}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>{opt}</option>
    ))}
  </select>
);

const MultiSelectChips = ({ values = [], onChange, options, testId }) => {
  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}
            data-testid={`${testId}-${opt.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

const TextAreaField = ({ value, onChange, placeholder, maxWords = 100, suggestKey, testId }) => {
  const count = wordCount(value);
  const over = count > maxWords;
  const suggestions = suggestKey ? SUGGESTIONS[suggestKey] || [] : [];

  const appendSuggestion = (word) => {
    const current = (value || '').trim();
    const next = current ? `${current}, ${word}` : word;
    onChange(next);
  };

  return (
    <div>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 ${over ? 'border-red-400' : ''}`}
        data-testid={testId}
      />
      <div className="flex items-center justify-between mt-1">
        <div className={`text-[11px] ${over ? 'text-red-500' : 'text-gray-400'}`}>
          {count} / {maxWords} words
        </div>
        {suggestions.length > 0 && (
          <div className="flex items-center gap-1 text-[11px] text-gray-400">
            <Sparkles size={12} /> suggested
          </div>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => appendSuggestion(s)}
              className="px-2 py-0.5 text-[11px] bg-gray-100 text-gray-600 rounded-full hover:bg-purple-100 hover:text-purple-700 transition-colors"
              data-testid={`${testId}-suggest-${s.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TagInput = ({ values = [], onChange, placeholder, testId }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((t) => (
          <span key={t} className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
            {t}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== t))} className="hover:bg-purple-200 rounded-full">
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300"
          data-testid={testId}
        />
        <button type="button" onClick={add} className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          Add
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function VeyonikaSessionSummary({ sessionId, isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [openSections, setOpenSections] = useState({ s1: true, s2: false, s3: false, s4: false, s5: false, s6: false, s7: false });

  const [formData, setFormData] = useState({
    preferred_name: '', alias_name: '', age_range: '', gender: 'Female',
    session_location: '', session_type: '',
    hormonal_life_stage: [], contextual_factors: [],
    primary_concern: '', secondary_concerns: '', duration_of_concern: '',
    trigger_onset_reasons: '', observed_emotional_state: '',
    intensity_level: '', affect_congruence: '', cognitive_patterns: '',
    physical_symptoms: '', emotional_behavioural_patterns: '',
    hormonal_awareness_level: '', hormonal_awareness_notes: '',
    key_themes: [], key_themes_notes: '',
    self_harm_ideation: '', suicidal_thoughts: '',
    abuse_violence_indicators: '', abuse_violence_notes: '',
    severe_distress_markers: '', severe_distress_notes: '',
    medical_red_flags: '', medical_red_flags_notes: '',
    overall_risk_level: '', action_taken: [], action_taken_notes: '',
    interventions_used: '', psychoeducation_provided: [], psychoeducation_notes: '',
    receptivity_level: '', insight_gained: '', emotional_shift: '',
    resistance_barriers: '', recommendations: '',
    follow_up_required: '', follow_up_timeline: '', follow_up_mode: '', follow_up_priority: '',
    session_quality_notes: '', what_worked_well: '', areas_to_explore_next: '',
    supervisor_escalation_needed: '', supervisor_escalation_note: '',
    stage_tag: '', concern_tags: [], severity_tag: '', user_type: '',
  });

  useEffect(() => {
    if (isOpen && sessionId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const sessionRes = await axios.get(`${API}/sessions/${sessionId}`, { headers });
      setSessionInfo(sessionRes.data);

      try {
        const res = await axios.get(`${API}/sessions/${sessionId}/veyonika-summary`, { headers });
        setExisting(res.data);
        const { id, session_id, provider_id, provider_name, summary_type, created_at, updated_at, client_id, case_id, session_date, session_start_time, planned_duration_minutes, ...editable } = res.data;
        setFormData((prev) => ({ ...prev, ...editable }));
      } catch (err) {
        if (err.response?.status === 404) {
          // No existing summary — fetch intake-driven prefill (Section 1)
          try {
            const prefillRes = await axios.get(
              `${API}/sessions/${sessionId}/veyonika-summary/prefill`,
              { headers },
            );
            const pf = prefillRes.data || {};
            // Only merge editable fields; auto-fill (client_id, case_id, etc.) is
            // surfaced via `autoBasics` below.
            setFormData((prev) => {
              const merged = { ...prev };
              Object.keys(pf).forEach((k) => {
                if (!(k in prev)) return; // skip auto-only keys
                const current = prev[k];
                const isBlank =
                  current === null ||
                  current === undefined ||
                  (typeof current === 'string' && !current.trim()) ||
                  (Array.isArray(current) && current.length === 0);
                if (isBlank) merged[k] = pf[k];
              });
              return merged;
            });
            // Also pass through the auto-fill basics so Section 1 read-only
            // fields show real values even before save.
            setSessionInfo((s) => ({
              ...(s || {}),
              masked_id: pf.client_id || s?.masked_id,
              session_duration_minutes: pf.planned_duration_minutes ?? s?.session_duration_minutes,
              created_at: s?.created_at,
              _prefill_case_id: pf.case_id,
              _prefill_session_date: pf.session_date,
              _prefill_session_start_time: pf.session_start_time,
            }));
          } catch (_) {
            // prefill is optional — silently fall back to blank Section 1
          }
        } else {
          throw err;
        }
      }
    } catch (e) {
      toast.error('Failed to load Veyonika summary');
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setFormData((p) => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (existing) {
        await axios.put(`${API}/sessions/${sessionId}/veyonika-summary`, formData, { headers });
        toast.success('Veyonika summary updated');
      } else {
        await axios.post(`${API}/sessions/${sessionId}/veyonika-summary`, formData, { headers });
        toast.success('Veyonika summary saved');
      }
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save Veyonika summary');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => setOpenSections((p) => ({ ...p, [key]: !p[key] }));

  if (!isOpen) return null;

  const autoBasics = existing || {
    client_id: sessionInfo?.masked_id || (sessionId ? `ANON-${sessionId.slice(0, 8).toUpperCase()}` : ''),
    case_id: sessionInfo?._prefill_case_id || '— generated on save —',
    session_date: sessionInfo?._prefill_session_date || sessionInfo?.created_at?.split('T')[0] || '',
    session_start_time: sessionInfo?._prefill_session_start_time || sessionInfo?.created_at?.split('T')[1]?.slice(0, 5) || '',
    planned_duration_minutes: sessionInfo?.session_duration_minutes || '',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="veyonika-summary-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-pink-100 via-purple-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-800" data-testid="veyonika-summary-title">
              Veyonika Session Summary
            </h2>
            <p className="text-xs text-gray-500">
              {existing ? `Updating record • ${autoBasics.case_id}` : 'New record — Veyonika team only'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/70 rounded-full" data-testid="veyonika-summary-close">
            <X size={22} className="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-purple-500" size={40} />
            </div>
          ) : (
            <>
              {/* ===== Section 1 ===== */}
              <Collapsible
                title="1. Client & Session Basics"
                subtitle="Auto-filled where possible"
                open={openSections.s1}
                onToggle={() => toggle('s1')}
                testId="veyonika-section-1"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Client ID (masked)</Label><TextInput value={autoBasics.client_id} readOnly onChange={() => {}} testId="veyonika-client-id" /></div>
                  <div><Label>Case ID</Label><TextInput value={autoBasics.case_id} readOnly onChange={() => {}} testId="veyonika-case-id" /></div>
                  <div><Label>Preferred Name</Label><TextInput value={formData.preferred_name} onChange={(v) => update('preferred_name', v)} placeholder="Client's preferred name" testId="veyonika-preferred-name" /></div>
                  <div><Label>Alias / Anonymized Name</Label><TextInput value={formData.alias_name} onChange={(v) => update('alias_name', v)} placeholder="Optional alias" testId="veyonika-alias-name" /></div>
                  <div><Label>Age Range</Label><SelectField value={formData.age_range} onChange={(v) => update('age_range', v)} options={AGE_RANGES} testId="veyonika-age-range" /></div>
                  <div><Label>Gender</Label><SelectField value={formData.gender} onChange={(v) => update('gender', v)} options={GENDER_OPTIONS} testId="veyonika-gender" /></div>
                  <div><Label>Session Location</Label><SelectField value={formData.session_location} onChange={(v) => update('session_location', v)} options={SESSION_LOCATIONS} testId="veyonika-session-location" /></div>
                  <div><Label>Session Type</Label><SelectField value={formData.session_type} onChange={(v) => update('session_type', v)} options={SESSION_TYPES} testId="veyonika-session-type" /></div>
                  <div><Label>Session Date</Label><TextInput value={autoBasics.session_date} readOnly onChange={() => {}} testId="veyonika-session-date" /></div>
                  <div><Label>Start Time</Label><TextInput value={autoBasics.session_start_time} readOnly onChange={() => {}} testId="veyonika-session-start-time" /></div>
                  <div><Label>Planned Duration (minutes)</Label><TextInput value={autoBasics.planned_duration_minutes?.toString() || ''} readOnly onChange={() => {}} testId="veyonika-planned-duration" /></div>
                </div>
              </Collapsible>

              {/* ===== Section 2 ===== */}
              <Collapsible title="2. Life Stage & Context" open={openSections.s2} onToggle={() => toggle('s2')} testId="veyonika-section-2">
                <div>
                  <Label>Hormonal Life Stage (multi-select)</Label>
                  <MultiSelectChips values={formData.hormonal_life_stage} onChange={(v) => update('hormonal_life_stage', v)} options={HORMONAL_STAGES} testId="veyonika-hormonal-stage" />
                </div>
                <div>
                  <Label>Contextual Factors (multi-select)</Label>
                  <MultiSelectChips values={formData.contextual_factors} onChange={(v) => update('contextual_factors', v)} options={CONTEXTUAL_FACTORS} testId="veyonika-contextual-factors" />
                </div>
              </Collapsible>

              {/* ===== Section 3 ===== */}
              <Collapsible title="3. Presenting Concerns & Emotional State" open={openSections.s3} onToggle={() => toggle('s3')} testId="veyonika-section-3">
                <div><Label required>Primary Concern</Label><TextAreaField value={formData.primary_concern} onChange={(v) => update('primary_concern', v)} placeholder="Client's main concern in their own words…" maxWords={100} testId="veyonika-primary-concern" /></div>
                <div><Label>Secondary Concerns</Label><TextAreaField value={formData.secondary_concerns} onChange={(v) => update('secondary_concerns', v)} placeholder="Client's secondary concern if any — communicated in their own words" maxWords={100} testId="veyonika-secondary-concerns" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Duration of Concern</Label><TextInput value={formData.duration_of_concern} onChange={(v) => update('duration_of_concern', v)} placeholder="e.g., 3 weeks, 6 months, >1 year" testId="veyonika-duration-concern" /></div>
                  <div><Label>Intensity Level</Label><SelectField value={formData.intensity_level} onChange={(v) => update('intensity_level', v)} options={INTENSITY_LEVELS} testId="veyonika-intensity-level" /></div>
                  <div><Label>Affect Congruence</Label><SelectField value={formData.affect_congruence} onChange={(v) => update('affect_congruence', v)} options={AFFECT_OPTIONS} testId="veyonika-affect-congruence" /></div>
                </div>
                <div><Label>Reasons of Trigger / Onset (as perceived by client)</Label><TextAreaField value={formData.trigger_onset_reasons} onChange={(v) => update('trigger_onset_reasons', v)} placeholder="What does the client feel triggered this?" maxWords={100} suggestKey="trigger_onset_reasons" testId="veyonika-trigger-reasons" /></div>
                <div><Label>Observed Emotional State</Label><TextAreaField value={formData.observed_emotional_state} onChange={(v) => update('observed_emotional_state', v)} placeholder="e.g., anxious, low, overwhelmed…" maxWords={100} suggestKey="observed_emotional_state" testId="veyonika-observed-state" /></div>
                <div><Label>Cognitive Patterns</Label><TextAreaField value={formData.cognitive_patterns} onChange={(v) => update('cognitive_patterns', v)} placeholder="e.g., rumination, self-doubt, catastrophizing" maxWords={100} suggestKey="cognitive_patterns" testId="veyonika-cognitive-patterns" /></div>
              </Collapsible>

              {/* ===== Section 4 ===== */}
              <Collapsible title="4. Hormonal Symptoms & Key Themes" open={openSections.s4} onToggle={() => toggle('s4')} testId="veyonika-section-4">
                <div><Label>Physical Symptoms Reported</Label><TextAreaField value={formData.physical_symptoms} onChange={(v) => update('physical_symptoms', v)} placeholder="e.g., fatigue, sleep issues, hot flashes, cramps" maxWords={100} suggestKey="physical_symptoms" testId="veyonika-physical-symptoms" /></div>
                <div><Label>Emotional / Behavioural Patterns Linked to Cycle & Life-Stage</Label><TextAreaField value={formData.emotional_behavioural_patterns} onChange={(v) => update('emotional_behavioural_patterns', v)} placeholder="Patterns across cycle / life stage" maxWords={100} suggestKey="emotional_behavioural_patterns" testId="veyonika-emotional-patterns" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Hormonal Awareness Level</Label><SelectField value={formData.hormonal_awareness_level} onChange={(v) => update('hormonal_awareness_level', v)} options={HORMONAL_AWARENESS} testId="veyonika-hormonal-awareness" /></div>
                </div>
                <div><Label>Hormonal Awareness Notes</Label><TextAreaField value={formData.hormonal_awareness_notes} onChange={(v) => update('hormonal_awareness_notes', v)} placeholder="Optional notes" maxWords={100} suggestKey="hormonal_awareness_notes" testId="veyonika-hormonal-awareness-notes" /></div>
                <div>
                  <Label>Key Themes (multi-select)</Label>
                  <MultiSelectChips values={formData.key_themes} onChange={(v) => update('key_themes', v)} options={KEY_THEMES} testId="veyonika-key-themes" />
                </div>
                <div><Label>Key Themes — short note</Label><TextInput value={formData.key_themes_notes} onChange={(v) => update('key_themes_notes', v)} placeholder="Optional short note" testId="veyonika-key-themes-notes" /></div>
              </Collapsible>

              {/* ===== Section 5 ===== */}
              <Collapsible title="5. Risk, Safety & Interventions" open={openSections.s5} onToggle={() => toggle('s5')} testId="veyonika-section-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Self-harm Ideation</Label><SelectField value={formData.self_harm_ideation} onChange={(v) => update('self_harm_ideation', v)} options={IDEATION_LEVELS} testId="veyonika-self-harm" /></div>
                  <div><Label>Suicidal Thoughts</Label><SelectField value={formData.suicidal_thoughts} onChange={(v) => update('suicidal_thoughts', v)} options={IDEATION_LEVELS} testId="veyonika-suicidal" /></div>
                  <div><Label>Abuse / Violence Indicators</Label><SelectField value={formData.abuse_violence_indicators} onChange={(v) => update('abuse_violence_indicators', v)} options={ABUSE_OPTIONS} testId="veyonika-abuse-indicators" /></div>
                  <div><Label>Severe Mental Distress Markers</Label><SelectField value={formData.severe_distress_markers} onChange={(v) => update('severe_distress_markers', v)} options={YES_NO} testId="veyonika-distress-markers" /></div>
                  <div><Label>Medical Red Flags</Label><SelectField value={formData.medical_red_flags} onChange={(v) => update('medical_red_flags', v)} options={YES_NO} testId="veyonika-medical-red-flags" /></div>
                  <div><Label>Overall Risk Level</Label><SelectField value={formData.overall_risk_level} onChange={(v) => update('overall_risk_level', v)} options={RISK_LEVELS} testId="veyonika-overall-risk" /></div>
                </div>
                <div><Label>Abuse / Violence Notes</Label><TextAreaField value={formData.abuse_violence_notes} onChange={(v) => update('abuse_violence_notes', v)} placeholder="Optional notes (≤50 words)" maxWords={50} suggestKey="abuse_violence_notes" testId="veyonika-abuse-notes" /></div>
                <div><Label>Severe Distress Notes</Label><TextAreaField value={formData.severe_distress_notes} onChange={(v) => update('severe_distress_notes', v)} placeholder="Optional notes (≤50 words)" maxWords={50} suggestKey="severe_distress_notes" testId="veyonika-distress-notes" /></div>
                <div><Label>Medical Red Flag Notes</Label><TextAreaField value={formData.medical_red_flags_notes} onChange={(v) => update('medical_red_flags_notes', v)} placeholder="Optional notes (≤50 words)" maxWords={50} suggestKey="medical_red_flags_notes" testId="veyonika-medical-notes" /></div>
                <div>
                  <Label>Action Taken (multi-select)</Label>
                  <MultiSelectChips values={formData.action_taken} onChange={(v) => update('action_taken', v)} options={ACTIONS_TAKEN} testId="veyonika-action-taken" />
                </div>
                <div><Label>Action Taken — notes</Label><TextAreaField value={formData.action_taken_notes} onChange={(v) => update('action_taken_notes', v)} placeholder="Details" maxWords={100} testId="veyonika-action-notes" /></div>
                <div><Label>Interventions Used</Label><TextAreaField value={formData.interventions_used} onChange={(v) => update('interventions_used', v)} placeholder="active listening, validation, psychoeducation, grounding, reframing, normalization, resource sharing, breathing / somatic practices" maxWords={100} suggestKey="interventions_used" testId="veyonika-interventions" /></div>
                <div>
                  <Label>Psychoeducation Provided (multi-select)</Label>
                  <MultiSelectChips values={formData.psychoeducation_provided} onChange={(v) => update('psychoeducation_provided', v)} options={PSYCHOEDUCATION} testId="veyonika-psychoeducation" />
                </div>
                <div><Label>Psychoeducation — notes</Label><TextAreaField value={formData.psychoeducation_notes} onChange={(v) => update('psychoeducation_notes', v)} placeholder="Optional notes" maxWords={100} testId="veyonika-psychoeducation-notes" /></div>
              </Collapsible>

              {/* ===== Section 6 ===== */}
              <Collapsible title="6. Client Engagement & Next Steps" open={openSections.s6} onToggle={() => toggle('s6')} testId="veyonika-section-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Receptivity Level</Label><SelectField value={formData.receptivity_level} onChange={(v) => update('receptivity_level', v)} options={RECEPTIVITY} testId="veyonika-receptivity" /></div>
                  <div><Label>Follow-up Required?</Label><SelectField value={formData.follow_up_required} onChange={(v) => update('follow_up_required', v)} options={YES_NO} testId="veyonika-followup-required" /></div>
                  <div><Label>Follow-up Mode</Label><SelectField value={formData.follow_up_mode} onChange={(v) => update('follow_up_mode', v)} options={FOLLOWUP_MODES} testId="veyonika-followup-mode" /></div>
                  <div><Label>Follow-up Priority</Label><SelectField value={formData.follow_up_priority} onChange={(v) => update('follow_up_priority', v)} options={INTENSITY_LEVELS} testId="veyonika-followup-priority" /></div>
                </div>
                <div><Label>Insight Gained</Label><TextAreaField value={formData.insight_gained} onChange={(v) => update('insight_gained', v)} placeholder="What did the client gain insight on?" maxWords={100} suggestKey="insight_gained" testId="veyonika-insight" /></div>
                <div><Label>Emotional Shift During Session</Label><TextAreaField value={formData.emotional_shift} onChange={(v) => update('emotional_shift', v)} placeholder="Shift observed during session" maxWords={100} suggestKey="emotional_shift" testId="veyonika-emotional-shift" /></div>
                <div><Label>Resistance / Barriers</Label><TextAreaField value={formData.resistance_barriers} onChange={(v) => update('resistance_barriers', v)} placeholder="Optional — barriers observed" maxWords={100} suggestKey="resistance_barriers" testId="veyonika-resistance" /></div>
                <div><Label>Recommendations & Guidance</Label><TextAreaField value={formData.recommendations} onChange={(v) => update('recommendations', v)} placeholder="Coping strategies, lifestyle adjustments, tracking, referrals, support groups" maxWords={100} testId="veyonika-recommendations" /></div>
                <div><Label>Follow-up Timeline</Label><TextAreaField value={formData.follow_up_timeline} onChange={(v) => update('follow_up_timeline', v)} placeholder="e.g., 2 weeks — suggested next: YYYY-MM-DD" maxWords={50} testId="veyonika-followup-timeline" /></div>
              </Collapsible>

              {/* ===== Section 7 ===== */}
              <Collapsible title="7. Counsellor Reflection & Tags (Internal)" open={openSections.s7} onToggle={() => toggle('s7')} testId="veyonika-section-7">
                <div><Label>Session Quality Notes</Label><TextAreaField value={formData.session_quality_notes} onChange={(v) => update('session_quality_notes', v)} placeholder="Internal reflection" maxWords={100} suggestKey="session_quality_notes" testId="veyonika-quality-notes" /></div>
                <div><Label>What Worked Well</Label><TextAreaField value={formData.what_worked_well} onChange={(v) => update('what_worked_well', v)} placeholder="Effective elements of this session" maxWords={100} suggestKey="what_worked_well" testId="veyonika-worked-well" /></div>
                <div><Label>Areas to Explore Next Time</Label><TextAreaField value={formData.areas_to_explore_next} onChange={(v) => update('areas_to_explore_next', v)} placeholder="Open threads to return to" maxWords={100} suggestKey="areas_to_explore_next" testId="veyonika-explore-next" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Supervisor Escalation Needed?</Label><SelectField value={formData.supervisor_escalation_needed} onChange={(v) => update('supervisor_escalation_needed', v)} options={YES_NO} testId="veyonika-escalation-needed" /></div>
                  <div><Label>Stage Tag</Label><SelectField value={formData.stage_tag} onChange={(v) => update('stage_tag', v)} options={STAGE_TAGS} testId="veyonika-stage-tag" /></div>
                  <div><Label>Severity Tag</Label><SelectField value={formData.severity_tag} onChange={(v) => update('severity_tag', v)} options={SEVERITY_TAGS} testId="veyonika-severity-tag" /></div>
                  <div><Label>First-time / Repeat User</Label><SelectField value={formData.user_type} onChange={(v) => update('user_type', v)} options={USER_TYPES} testId="veyonika-user-type" /></div>
                </div>
                <div><Label>Supervisor Escalation Note</Label><TextInput value={formData.supervisor_escalation_note} onChange={(v) => update('supervisor_escalation_note', v)} placeholder="Optional note" testId="veyonika-escalation-note" /></div>
                <div>
                  <Label>Concern Tags (free-text taxonomy)</Label>
                  <TagInput values={formData.concern_tags} onChange={(v) => update('concern_tags', v)} placeholder="Type a tag and press Enter" testId="veyonika-concern-tags" />
                </div>
              </Collapsible>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-white">
          <p className="text-xs text-gray-500">
            {existing ? `Last saved: ${new Date(existing.updated_at || existing.created_at).toLocaleString()}` : 'Not saved yet'}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg" data-testid="veyonika-summary-cancel">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="px-5 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="veyonika-summary-save"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {existing ? 'Update Veyonika Summary' : 'Save Veyonika Summary'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
